import { AxiosError, type AxiosResponse } from "axios";
import type {
  CollectionClient,
  CollectionMaterialRecord,
  CollectionRecord,
  MaterialRef,
  ProjectOption,
} from "./collection-client.types";

export interface MockCollectionClientOptions {
  /** Collections the caller already owns. */
  collections?: Array<{ id: string; name: string }>;
  /** Projects the caller can write to. */
  projects?: ProjectOption[];
  /** Simulated network latency per call, in ms. */
  latencyMs?: number;
}

export const MOCK_PROJECTS: ProjectOption[] = [
  { id: "prj-ev-charger", name: "EV Charging Station Cover" },
  { id: "prj-dashboard", name: "Dashboard Bezel" },
  { id: "prj-headphones", name: "Headphone Shell" },
];

export const MOCK_COLLECTIONS: Array<{ id: string; name: string }> = [
  { id: "col-material-mix", name: "Material Mix" },
  { id: "col-soft-touch", name: "Soft Touch Surfaces" },
  { id: "col-sustainable", name: "Sustainable Futures" },
  { id: "col-automotive", name: "Automotive Interiors 2027" },
  { id: "col-consumer", name: "consumer electronics" },
  { id: "col-bright", name: "Bright Accents" },
  { id: "col-medical", name: "Medical Devices" },
];

const delay = (ms: number) =>
  ms > 0
    ? new Promise((resolve) => setTimeout(resolve, ms))
    : Promise.resolve();

const materialKey = (material: MaterialRef) =>
  `${material.sourceDiscriminator}:${material.sourceRefId}`;

/** In-memory {@link CollectionClient} until CX-3786 wires the HTTP client. */
export const createMockCollectionClient = ({
  collections = MOCK_COLLECTIONS,
  projects = MOCK_PROJECTS,
  latencyMs = 0,
}: MockCollectionClientOptions = {}): CollectionClient => {
  const store = new Map<string, CollectionRecord>(
    collections.map((c) => [
      c.id,
      { ...c, createdAt: new Date(0).toISOString() },
    ]),
  );
  const memberships = new Map<string, CollectionMaterialRecord>();
  let sequence = 0;
  const nextId = (prefix: string) => `${prefix}-${++sequence}`;

  const findMembership = (collectionId: string, material: MaterialRef) =>
    Array.from(memberships.values()).find(
      (m) =>
        m.collectionId === collectionId &&
        materialKey(m) === materialKey(material),
    );

  return {
    async getMembership(material) {
      await delay(latencyMs);
      return Array.from(store.values()).map((collection) => {
        const membership = findMembership(collection.id, material);
        return {
          collectionId: collection.id,
          name: collection.name,
          contains: membership !== undefined,
          membershipId: membership?.membershipId,
        };
      });
    },

    async addMaterial(collectionId, material) {
      await delay(latencyMs);
      if (!store.has(collectionId)) {
        throw new Error(`Collection ${collectionId} not found`);
      }
      const existing = findMembership(collectionId, material);
      if (existing) {
        return existing;
      }
      const record: CollectionMaterialRecord = {
        membershipId: nextId("membership"),
        collectionId,
        sourceDiscriminator: material.sourceDiscriminator,
        sourceRefId: material.sourceRefId,
        createdAt: new Date().toISOString(),
      };
      memberships.set(record.membershipId, record);
      return record;
    },

    async removeMaterial(collectionId, membershipId) {
      await delay(latencyMs);
      const record = memberships.get(membershipId);
      if (record?.collectionId === collectionId) {
        memberships.delete(membershipId);
      }
    },

    async createCollection({ name, linkedProjectIds = [] }) {
      await delay(latencyMs);
      if (linkedProjectIds.some((id) => !projects.some((p) => p.id === id))) {
        // The service's 404 shape, so `getHttpStatus` reads it the same way.
        throw new AxiosError(
          "Project not found",
          "ERR_BAD_REQUEST",
          undefined,
          undefined,
          { status: 404 } as AxiosResponse,
        );
      }
      const record: CollectionRecord = {
        id: nextId("collection"),
        name,
        createdAt: new Date().toISOString(),
      };
      store.set(record.id, record);
      return record;
    },

    async listProjectOptions() {
      await delay(latencyMs);
      return [...projects];
    },

    async addMoodboardImage(collectionId) {
      await delay(latencyMs);
      if (!store.has(collectionId)) {
        throw new Error(`Collection ${collectionId} not found`);
      }
    },
  };
};
